import React, { useMemo, useState } from 'react';
import { Button, DropdownMenu, InputSingleSelect, SparkIcon, Spinner, TooltipTrigger } from '@spark';
import { TaskType } from '@/shared/domain/taskTypes';
import { HelpIcon } from '../shared/TaskTypeControls';

const sortAbc = (labels) =>
    [...(labels || [])].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));

/**
 * Warning icon + tooltip shown inside the Replace trigger when the mapped
 * value is not one of the labels configured in task creation (Figma 3088-7897).
 */
const RowWarningIcon = ({ mapped, isNer }) => (
    <TooltipTrigger
        text={`"${mapped}" is not one of the ${isNer ? 'entity types' : 'labels'} defined in task creation. Select an existing ${isNer ? 'entity type' : 'label'}, or add it on the Business Goals step.`}
    >
        <span className="cw-dc-mapping-row__warning-icon">
            <SparkIcon slug="circle-alert-triangle" size="small" />
        </span>
    </TooltipTrigger>
);

/**
 * Task-membership toggle on Replace dropdown rows — a trash icon on in-task
 * rows (removes the label from the task) and a plus icon on file-only rows
 * (adds the label to the task).
 */
const TaskLabelTargetToggle = ({ inTask, isNer, onToggle }) => {
    const noun = isNer ? 'entity' : 'label';
    const actionText = inTask ? `Remove ${noun} from the task` : `Add ${noun} to the task`;
    return (
        <TooltipTrigger text={actionText}>
            <button
                type="button"
                className="cw-dc-label-target"
                aria-label={actionText}
                aria-pressed={inTask}
                onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    onToggle();
                }}
                onMouseDown={(e) => {
                    // Keep the searchable panel open; outside-click closes on mousedown.
                    e.preventDefault();
                    e.stopPropagation();
                }}
            >
                <SparkIcon slug={inTask ? 'trash' : 'plus'} size="small" />
            </button>
        </TooltipTrigger>
    );
};

/**
 * Searchable Replace select with task-label / file-label sections.
 * Clicking a file-only row (or its plus icon) adds that label to the task.
 * In-task rows only pick a mapping; the trash icon is the only remove action.
 */
const ReplaceLabelSelect = ({
    showHeaders,
    mapped,
    isUnknownLabel,
    isNer,
    taskLabels,
    fileLabels,
    onReplace,
    onToggleTaskLabel,
}) => {
    const [open, setOpen] = useState(false);

    const sections = useMemo(() => {
        const toItem = (option, inTask) => ({
            label: option,
            state: option === mapped ? 'selected' : 'default',
            onClick: () => {
                if (!inTask) {
                    onToggleTaskLabel(option);
                }
                onReplace(option);
                setOpen(false);
            },
            suffix: (
                <span className="spark-dropdown-item-suffix">
                    <TaskLabelTargetToggle
                        inTask={inTask}
                        isNer={isNer}
                        onToggle={() => onToggleTaskLabel(option)}
                    />
                </span>
            ),
        });
        const noun = isNer ? 'Entities' : 'Labels';
        const next = [];
        if (taskLabels.length) {
            next.push({
                title: `${noun} defined in ‘Business Goals’ (last step)`,
                items: taskLabels.map((label) => toItem(label, true)),
            });
        }
        if (fileLabels.length) {
            next.push({
                title: `${noun} found in file`,
                items: fileLabels.map((label) => toItem(label, false)),
            });
        }
        return next;
    }, [taskLabels, fileLabels, mapped, isNer, onToggleTaskLabel, onReplace]);

    return (
        <DropdownMenu
            selectType="single-select"
            size="medium"
            label={
                <>
                    {isNer ? 'Select Entity' : 'Select Label'}
                    <HelpIcon
                        text={`Help us map the ${isNer ? 'entity' : 'label'} name from your uploaded file. Resolve this by selecting the closest option`}
                    />
                </>
            }
            showLabel={showHeaders}
            text={mapped}
            value={mapped}
            open={open}
            onOpenChange={setOpen}
            searchable
            searchMode="trigger"
            searchEmptyText="No matching labels"
            prefixIcon={
                isUnknownLabel ? <RowWarningIcon mapped={mapped} isNer={isNer} /> : undefined
            }
            sections={sections}
        />
    );
};

/**
 * Extracted-label mapping rows for the wizard Data Collection step.
 *
 * Classification & NER (Figma 3086-6576): disabled "Extracted" select +
 * "Replace" select + trash, both selects flex-1 in the controls column.
 *
 * Replace options: all task labels (ABC) under "… defined in ‘Business Goals’
 * (last step)", plus file-extracted labels not already on the task under
 * "… found in file". File-only rows (and their plus icons) add a label to the
 * task; the trash icon on in-task rows is the only way to remove one.
 */
const LabelMappingRows = ({
    taskType,
    extractedLabels,
    mappings,
    availableLabels,
    replaceOptions,
    loading,
    onReplace,
    onDelete,
    onToggleTaskLabel,
}) => {
    const isNer = taskType === TaskType.NER;

    const taskLabels = useMemo(() => sortAbc(availableLabels), [availableLabels]);

    const fileLabels = useMemo(() => {
        const taskSet = new Set(availableLabels || []);
        return sortAbc((replaceOptions || []).filter((label) => !taskSet.has(label)));
    }, [replaceOptions, availableLabels]);

    if (loading) {
        return (
            <div className="cw-dc-mapping-loading">
                <Spinner size="small" />
                <span>Extracting {isNer ? 'entities' : 'labels'}…</span>
            </div>
        );
    }

    const rows = (extractedLabels || []).filter((label) => mappings[label] !== null);
    if (!rows.length) return null;

    const isUnknown = (label) => !(availableLabels || []).includes(mappings[label] ?? label);
    // The walkthrough's "Map unmatched labels" coach mark anchors to the first
    // row whose file label is not (yet) one of the task labels.
    const walkthroughRow = rows.find(isUnknown);

    return (
        <div className="cw-dc-mapping-rows">
            {rows.map((label, index) => {
                const mapped = mappings[label] ?? label;
                const showHeaders = index === 0;
                const isUnknownLabel = isUnknown(label);
                return (
                    <React.Fragment key={label}>
                        {index > 0 && <div className="cw-dc-divider" />}
                        <div className="cw-dc-mapping-row">
                            <div className="cw-dc-mapping-row__select">
                                <InputSingleSelect
                                    label="Extracted"
                                    showLabel={showHeaders}
                                    text={label}
                                    disabled
                                />
                            </div>
                            <div
                                className="cw-dc-mapping-row__select"
                                data-walkthrough={label === walkthroughRow ? 'label-mapping' : undefined}
                            >
                                <ReplaceLabelSelect
                                    showHeaders={showHeaders}
                                    mapped={mapped}
                                    isUnknownLabel={isUnknownLabel}
                                    isNer={isNer}
                                    taskLabels={taskLabels}
                                    fileLabels={fileLabels}
                                    onReplace={(next) => onReplace(label, next)}
                                    onToggleTaskLabel={onToggleTaskLabel}
                                />
                            </div>
                            <div
                                className={`cw-dc-mapping-row__trash${showHeaders ? ' cw-dc-mapping-row__trash--offset' : ''}`}
                            >
                                <Button
                                    size="tiny"
                                    variant="ghost"
                                    icon={<SparkIcon slug="trash" size="small" />}
                                    aria-label={`Remove ${isNer ? 'entity' : 'label'} ${label}`}
                                    onClick={() => onDelete(label)}
                                />
                            </div>
                        </div>
                    </React.Fragment>
                );
            })}
        </div>
    );
};

export default LabelMappingRows;
